import api from '@/libs/api'
import store from '@/store'

const getProjectId = () => store.state.app.projectId

export default {
  namespaced: true,
  state: {
    initializingLiveHubs: false,
    liveHubs: [],
    fetchingLiveHubs: false,
    fetchingLiveHubsStatuses: false,
    liveHub: null,
    liveHubStatuses: [],
    creatingLiveHub: false,
    gettingLiveHub: false,
    updatingLiveHub: false,
    liveHubsResultInfo: {},
    resultInfo: {},
    currentPage: 1,
    wizardActiveTab: null,
    gjBlob: null,
    settingTranscodingStatus: false,
    selectedLiveJobs: [],
    removingLiveHub: false,
  },
  getters: {
    currentPage: state => state.currentPage,
  },
  mutations: {
    SET_INITIALIZING_LIVE_HUBS(state, value) {
      state.initializingLiveHubs = value
    },
    SET_LIVE_HUBS(state, res) {
      state.liveHubs = res.result
      state.liveHubsResultInfo = res.resultInfo
    },
    SET_LIVE_HUB(state, value) {
      state.liveHub = value
      state.liveHub.recordTypes = {
        RecordAllStream: 0,
        RecordHourlyStream: 1,
      }
    },
    SET_LIVE_HUB_STATUSES(state, value) {
      state.liveHubStatuses = value
    },
    SET_FETCHING_LIVE_HUBS(state, value) {
      state.fetchingLiveHubs = value
    },
    SET_FETCHING_LIVE_HUBS_STATUSES(state, value) {
      state.fetchingLiveHubsStatuses = value
    },
    SET_GETTING_LIVE_HUB(state, value) {
      state.gettingLiveHub = value
    },
    SET_UPDATING_LIVE_HUB(state, value) {
      state.updatingLiveHub = value
    },
    SET_CREATING_LIVE_HUB(state, value) {
      state.creatingLiveHub = value
    },
    SET_CURRENT_PAGE(state, value) {
      state.currentPage = value
    },
    SET_WIZARD_ACTIVE_TAB(state, value) {
      state.wizardActiveTab = value
    },
    SET_GJBLOB(state, value) {
      state.gjBlob = value
    },
    SET_DESTINATION(state, value) {
      state.liveHub.destination = value
    },
    SET_SELECTED_LIVE_JOBS(state, value) {
      state.selectedLiveJobs = value
    },
    SET_TRANSCODING_STATUS(state, value) {
      state.settingTranscodingStatus = value
    },
    REMOVE_LIVE_HUB(state, id) {
      state.liveHubs = state.liveHubs.filter(h => h.name !== id)
    },
    SET_REMOVING_LIVE_HUB(state, value) {
      state.removingLiveHub = value
    },
  },
  actions: {
    getLiveHub({ commit }, liveHubName) {
      commit('SET_GETTING_LIVE_HUB', true)
      return new Promise((resolve, reject) => {
        api('encoder')
          .get(`projects/${getProjectId()}/livehubs/${liveHubName}`)
          .then(response => {
            commit('SET_LIVE_HUB', response.data.result)
            commit('SET_SELECTED_LIVE_JOBS', response.data.result.liveJobs)
            resolve(response)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_GETTING_LIVE_HUB', false)
          })
      })
    },
    fetchLiveHubs({ commit }, payload) {
      const pageIndex = `pageIndex=${payload.pageIndex || 0}`
      const status = `status=${payload.status}`
      const search = `${payload.search ? `&search=${payload.search}` : ''}`
      const query = `?pageSize=10&${pageIndex}${search}&${status}`
      commit('SET_FETCHING_LIVE_HUBS', true)
      return new Promise((resolve, reject) => {
        api('encoder')
          .get(`projects/${getProjectId()}/livehubs${query}`)
          .then(response => {
            commit('SET_LIVE_HUBS', response.data)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_FETCHING_LIVE_HUBS', false)
          })
      })
    },
    modifyLiveHub({ commit }, query) {
      commit('SET_UPDATING_LIVE_HUB', true)
      return new Promise((resolve, reject) => {
        api('encoderV2')
          .put(`projects/${getProjectId()}/livehubs/${query.liveHubName}`, query.liveHub)
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_UPDATING_LIVE_HUB', false)
          })
      })
    },
    fetchLiveHubsStatuses({ commit }) {
      commit('SET_FETCHING_LIVE_HUBS_STATUSES', true)
      return new Promise((resolve, reject) => {
        api('encoder')
          .get(`projects/${getProjectId()}/livejobs/statuses`)
          .then(response => {
            const statuses = response.data.jobStatusOptions
            statuses.sort((a, b) => a.id - b.id)
            commit('SET_LIVE_HUB_STATUSES', statuses)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_FETCHING_LIVE_HUBS_STATUSES', false)
          })
      })
    },
    removeLiveHub({ commit }, query) {
      commit('SET_REMOVING_LIVE_HUB', true)
      return new Promise((resolve, reject) => {
        api(`encoder${query.version}`).delete(`/projects/${getProjectId()}/livehubs/${query.liveHubId}`)
          .then(response => {
            commit('REMOVE_LIVE_HUB', query.liveHubId)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => { commit('SET_REMOVING_LIVE_HUB', false) })
      })
    },
    updateLiveHubStatus({ commit }, query) {
      commit('SET_UPDATING_LIVE_HUB', true)
      return new Promise((resolve, reject) => {
        const body = [
          {
            op: 'replace',
            path: 'ActiveLiveJobName',
            value: query.activeLiveJobName,
          },
        ]
        api(`encoder${query.version}`)
          .patch(`/projects/${getProjectId()}/livehubs/${query.liveHubName}`, body)
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_UPDATING_LIVE_HUB', false)
          })
      })
    },
    updateTranscoding({ commit }, query) {
      commit('SET_TRANSCODING_STATUS', true)
      return new Promise((resolve, reject) => {
        api(`encoder${query.version}`)
          .post(`/projects/${getProjectId()}/livehubs/${query.name}/toggle`)
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_TRANSCODING_STATUS', false)
          })
      })
    },

    // Live Hubs V2 API
    getLiveHubV2({ commit }, liveHubName) {
      commit('SET_GETTING_LIVE_HUB', true)
      return new Promise((resolve, reject) => {
        api('encoderV2')
          .get(`projects/${getProjectId()}/livehubs/${liveHubName}`)
          .then(response => {
            commit('SET_LIVE_HUB', response.data.result)
            commit('SET_SELECTED_LIVE_JOBS', response.data.result.liveJobs)
            resolve(response)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_GETTING_LIVE_HUB', false)
          })
      })
    },
    fetchLiveHubsV2({ commit }, payload) {
      const pageIndex = `pageIndex=${payload.pageIndex || 0}`
      const status = `status=${payload.status}`
      const search = `${payload.search ? `&search=${payload.search}` : ''}`
      const query = `?pageSize=10&${pageIndex}${search}&${status}`
      commit('SET_FETCHING_LIVE_HUBS', true)
      return new Promise((resolve, reject) => {
        api('encoderV2')
          .get(`projects/${getProjectId()}/livehubs${query}`)
          .then(response => {
            commit('SET_LIVE_HUBS', response.data)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_FETCHING_LIVE_HUBS', false)
          })
      })
    },
    createLiveHubV2({ commit }, hub) {
      commit('SET_CREATING_LIVE_HUB', true)
      return new Promise((resolve, reject) => {
        api('encoderV2')
          .post(`projects/${getProjectId()}/livehubs`, hub)
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_CREATING_LIVE_HUB', false)
          })
      })
    },
  },
}
